import React, { useMemo, useState } from "react";

import { Callout } from "../../components/Callout";
import { cn } from "../../components/cn";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, SearchIcon } from "../../components/icons";
import type { TallyChart, TallyChartLedger } from "../../data/tallyLedgerApi";
import { buildTallyChartSections, type TallyChartNode, type TallyChartSection } from "../../domain/tallyChart";

function LedgerRow({ ledger, nested }: { ledger: TallyChartLedger; nested?: boolean }) {
  return (
    <li className={cn("flex items-center gap-1.5 text-sm", nested && "pl-5")}>
      <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <span className="truncate">{ledger.name}</span>
      {ledger.is_group && (
        <span className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
          group
        </span>
      )}
    </li>
  );
}

function LedgerBranch({ node, nested }: { node: TallyChartNode; nested?: boolean }) {
  return (
    <li className={cn(!nested && "mb-1 break-inside-avoid")}>
      <ul className="flex flex-col gap-1">
        <LedgerRow ledger={node.ledger} nested={nested} />
        {node.children.map((child) => (
          <LedgerBranch key={child.ledger.account_id || child.ledger.guid || child.ledger.name} node={child} nested />
        ))}
      </ul>
    </li>
  );
}

function Section({
  section,
  open,
  onToggle,
}: {
  section: TallyChartSection;
  open: boolean;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <li className="flex flex-col py-2 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 py-1 text-left text-sm font-medium"
      >
        <Chevron className="size-4 text-muted-foreground" />
        {section.label} ({section.total})
      </button>
      {open && (
        <ul className="pb-1 pl-6 pt-1 sm:columns-2 sm:gap-6">
          {section.nodes.map((node) => (
            <LedgerBranch key={node.ledger.account_id || node.ledger.guid || node.ledger.name} node={node} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function TallyCurrentLedgers({
  chart,
  clientName,
}: {
  chart: TallyChart | null;
  clientName: string;
}) {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  const query = search.trim().toLowerCase();

  const sections = useMemo(
    () => buildTallyChartSections(chart?.ledgers ?? [], chart?.banks ?? [], query),
    [chart, query],
  );
  const total = (chart?.ledgers.length ?? 0) + (chart?.banks.length ?? 0);

  function toggle(slug: string) {
    const slugs = buildTallyChartSections(chart?.ledgers ?? [], chart?.banks ?? [], "").map((section) => section.slug);
    setCollapsed((previous) => {
      const next = new Set(previous ?? slugs);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h3 className="text-base font-medium">
        Current ledgers
        {chart !== null && <span className="ml-2 text-xs font-normal text-muted-foreground">{total} total</span>}
      </h3>

      {chart === null ? (
        <p className="text-sm text-muted-foreground">Reading the chart of accounts…</p>
      ) : total === 0 ? (
        <Callout variant="warning">
          No ledgers were read from {clientName}&apos;s Tally company. Go back to the Tally connection and re-run the
          read if the company is open.
        </Callout>
      ) : (
        <>
          <label className="relative flex items-center">
            <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search ledgers"
              className="h-9 w-full rounded-md border bg-background pl-8 pr-3 text-sm"
            />
          </label>

          {sections.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ledgers match &quot;{search.trim()}&quot;.</p>
          ) : (
            <ul className="flex flex-col divide-y">
              {sections.map((section) => (
                <Section
                  key={section.slug}
                  section={section}
                  open={query !== "" || (collapsed !== null && !collapsed.has(section.slug))}
                  onToggle={() => toggle(section.slug)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
